import { useEffect, useMemo } from "react";
import { useAudit } from "./AuditContext";
import { buildPatternRead } from "./patternEngine";

/**
 * Room configurations
 * -------------------
 * Each room declares:
 *   camera   — one of CAMERA_PRESETS ("wide" | "whiteboard" | "floor" | "desk")
 *   pose     — a semantic pose (see AuditContext.DEFAULT_PRESENTER.poses)
 *   message  — placeholder line the presenter says
 *   Panel    — the room's interactive overlay panel (React component)
 *
 * Panels are rendered inside a shared <RoomOverlay> which floats above the
 * Stage as a translucent card. Real interaction UIs land here later — the
 * Stage, camera, presenter and speech bubble stay untouched.
 */

// ---------- Shared overlay panel --------------------------------------------
const RoomOverlay = ({ number, kicker, title, children, side = "right" }) => (
  <div
    className={`absolute inset-y-0 flex items-center pointer-events-none px-4 md:px-8 lg:px-14 ${
      side === "right" ? "right-0 justify-end" : "left-0 justify-start"
    }`}
    data-testid={`overlay-${kicker.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className="pointer-events-auto w-full max-w-[520px] mr-5 bg-paper/92 backdrop-blur-md border border-ink/12 shadow-[0_18px_48px_rgba(0,0,0,0.35)] px-6 md:px-8 py-6 md:py-8">
      <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-terracotta mb-3">
        Room {String(number).padStart(2, "0")} · {kicker}
      </p>
      <h2 className="font-serif text-2xl md:text-3xl leading-[1.1] text-ink">{title}</h2>
      <div className="mt-5">{children}</div>
    </div>
  </div>
);

const DemoChoice = ({ roomId, choiceId, label, tag }) => {
  const { answers, setRoomAnswer, addTags } = useAudit();
  const selected = answers[roomId]?.choice === choiceId;
  return (
    <button
      type="button"
      onClick={() => {
        setRoomAnswer(roomId, { choice: choiceId, label });
        if (tag) addTags([tag]);
      }}
      data-testid={`${roomId}-choice-${choiceId}`}
      className={`w-full text-left px-4 py-3 border transition-colors ${
        selected
          ? "border-terracotta bg-paper-deep/60 text-ink"
          : "border-ink/15 hover:border-ink/40 text-ink-soft"
      }`}
    >
      <span className="font-mono text-xs uppercase tracking-[0.14em] mr-3">{choiceId}</span>
      <span className="font-serif text-base md:text-lg">{label}</span>
    </button>
  );
};

// ---------- Message Room 01 — sticky-note whiteboard interaction -----------
const MESSAGE_NOTES = [
  { id: "audience",   label: "Audience",              hint: "Who am I speaking to?" },
  { id: "goal",       label: "Goal",                  hint: "What am I trying to achieve?" },
  { id: "think",      label: "Think",                 hint: "What should they think differently?" },
  { id: "feel",       label: "Feel",                  hint: "What should they feel?" },
  { id: "do",         label: "Do",                    hint: "What should they do afterwards?" },
  { id: "coreIdea",   label: "Core Idea",             hint: "What is the one idea to remember?" },
  { id: "whyNow",     label: "Why Now",               hint: "Why does this matter now?" },
  { id: "stakes",     label: "Stakes",                hint: "What matters to them?" },
  { id: "whatToSay",  label: "What I Want to Say",    hint: "What do I want to communicate?" },
  { id: "evidence",   label: "Evidence",              hint: "What facts or data do I need to include?" },
  { id: "coverage",   label: "Content Coverage",      hint: "What information must I cover?" },
  { id: "time",       label: "Time",                  hint: "How much time do I have?" },
];

// Step-by-step intro sequence — user taps to advance. Short coach-like lines.
const NOTES_INTRO_STEPS = [
  "Before you build a message\u2026",
  "\u2026decide what deserves your attention first.",
  "You\u2019ll see a set of notes on the board.",
  "Move five into \u2018For Now\u2019.",
  "The rest can wait.",
  "When you\u2019re ready, begin.",
];
// Success message — shown one line at a time after 5 notes are in FOR NOW.
const NOTES_SUCCESS_STEPS = [
  "Good.",
  "What we notice shapes the message.",
  "Now let\u2019s see how you organise it.",
];
const FOR_NOW_MAX = 5;

const NOTE_ROTATIONS = [-2.5, 1.4, -1.2, 2.1, -0.6, 1.8, -2.0, 0.7, -1.7, 2.3, -0.9, 1.1];

// Small red push-pin — matches the mockup.
const Pin = () => (
  <span
    aria-hidden="true"
    className="absolute -top-1.5 left-1/2 -translate-x-1/2 z-10"
    style={{ width: 12, height: 12 }}
  >
    <span
      className="block w-full h-full rounded-full"
      style={{
        background:
          "radial-gradient(circle at 35% 30%, #ff5b4c 0%, #c62b1a 55%, #7a1206 100%)",
        boxShadow: "0 1.5px 2px rgba(0,0,0,0.45), inset 0 -1px 1px rgba(0,0,0,0.35)",
      }}
    />
  </span>
);

// Yellow post-it note pinned to the whiteboard.
function Note({ note, index, zone, interactive, onTap }) {
  const rotation = NOTE_ROTATIONS[index % NOTE_ROTATIONS.length];
  return (
    <button
      type="button"
      draggable={interactive}
      onDragStart={(e) => {
        if (!interactive) return;
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", note.id);
      }}
      onClick={() => interactive && onTap?.(note.id)}
      disabled={!interactive}
      data-testid={`note-${note.id}`}
      data-zone={zone}
      className={[
        "relative text-left w-[72px] md:w-[78px] min-h-[58px] md:min-h-[62px]",
        "px-1.5 py-1 transition-all duration-200",
        interactive ? "cursor-grab active:cursor-grabbing hover:-translate-y-0.5 hover:rotate-0" : "cursor-default",
      ].join(" ")}
      style={{
        background:
          zone === "forNow"
            ? "linear-gradient(180deg, #FFE45C 0%, #F7C93A 100%)"
            : "linear-gradient(180deg, #FFEA6A 0%, #FBD84D 100%)",
        boxShadow:
          zone === "forNow"
            ? "0 6px 14px rgba(60,40,10,0.35), inset 0 -3px 0 rgba(0,0,0,0.06)"
            : "0 4px 10px rgba(60,40,10,0.28), inset 0 -3px 0 rgba(0,0,0,0.06)",
        transform: `rotate(${rotation}deg)`,
      }}
    >
      <Pin />
      <p className="font-serif text-[10px] md:text-[11px] leading-tight text-ink pt-0.5">
        {note.label}
      </p>
      {note.hint && (
        <p className="mt-0.5 font-body text-[8px] leading-tight text-ink/75">
          {note.hint}
        </p>
      )}
    </button>
  );
}

// A drop bucket on the right side — bucket has its own background colour.
// Notes (still yellow) sit inside as a mini-grid so the visual metaphor of
// "moving notes into a coloured pile" is preserved.
function Bucket({ id, title, count, cap, notes, indexOf, onDrop, onTap, interactive, bgFrom, bgTo, borderColor }) {
  const capReached = typeof cap === "number" && count >= cap;
  return (
    <div
      onDragOver={(e) => interactive && !capReached && e.preventDefault()}
      onDrop={(e) => {
        if (!interactive) return;
        e.preventDefault();
        const nid = e.dataTransfer.getData("text/plain");
        if (nid) onDrop(nid, id);
      }}
      data-testid={`zone-${id}`}
      className="relative flex-1 min-h-0 px-2.5 py-2 flex flex-col"
      style={{
        background: `linear-gradient(160deg, ${bgFrom} 0%, ${bgTo} 100%)`,
        border: `1.5px solid ${borderColor}`,
        boxShadow: "inset 0 2px 6px rgba(0,0,0,0.18), 0 2px 4px rgba(0,0,0,0.15)",
      }}
    >
      <div className="flex items-baseline justify-between mb-1">
        <p
          className="font-mono uppercase tracking-[0.24em] text-paper"
          style={{ fontSize: "9px", textShadow: "0 1px 2px rgba(0,0,0,0.45)" }}
        >
          {title}
        </p>
        {typeof cap === "number" && (
          <p
            data-testid={`zone-${id}-counter`}
            className="font-mono text-paper"
            style={{ fontSize: "10px", textShadow: "0 1px 2px rgba(0,0,0,0.45)" }}
          >
            {count}/{cap}
          </p>
        )}
      </div>
      <div className="flex-1 min-h-0 flex flex-wrap gap-1 items-start content-start overflow-hidden">
        {notes.map((n) => (
          <Note
            key={n.id}
            note={n}
            index={indexOf(n.id)}
            zone={id}
            interactive={interactive}
            onTap={onTap}
          />
        ))}
      </div>
    </div>
  );
}

// Left half — the pool of available notes as a grid on the whiteboard.
function SourceGrid({ notes, indexOf, onDrop, onTap, interactive }) {
  return (
    <div
      onDragOver={(e) => interactive && e.preventDefault()}
      onDrop={(e) => {
        if (!interactive) return;
        e.preventDefault();
        const nid = e.dataTransfer.getData("text/plain");
        if (nid) onDrop(nid, "source");
      }}
      data-testid="zone-source"
      className="relative flex-1 min-h-0 px-2 py-2 flex flex-col"
      style={{
        background: "rgba(0,0,0,0.03)",
        border: "1.5px dashed rgba(60,45,25,0.35)",
      }}
    >
      <p
        className="font-mono uppercase tracking-[0.24em] text-ink/70 mb-1"
        style={{ fontSize: "9px" }}
      >
        Available notes
      </p>
      <div className="flex-1 min-h-0 flex flex-wrap gap-1.5 items-start content-start overflow-hidden">
        {notes.map((n) => (
          <Note
            key={n.id}
            note={n}
            index={indexOf(n.id)}
            zone="source"
            interactive={interactive}
            onTap={onTap}
          />
        ))}
      </div>
    </div>
  );
}

// -------- Renders INSIDE the Stage camera transform --------------------------
export function MessageBoardOverlay() {
  const { answers, setRoomAnswer, setTagsForRoom, canProceed } = useAudit();
  const forNow = answers.message?.forNow || [];
  const forLater = answers.message?.forLater || [];
  const source = MESSAGE_NOTES
    .filter((n) => !forNow.includes(n.id) && !forLater.includes(n.id))
    .map((n) => n.id);

  const indexOf = (id) => MESSAGE_NOTES.findIndex((n) => n.id === id);
  const noteById = (id) => MESSAGE_NOTES.find((n) => n.id === id);

  const move = (noteId, targetZone) => {
    const currentZone = forNow.includes(noteId)
      ? "forNow"
      : forLater.includes(noteId)
      ? "forLater"
      : "source";
    if (currentZone === targetZone) return;
    if (targetZone === "forNow" && forNow.length >= FOR_NOW_MAX) return;

    let nextForNow = forNow.filter((id) => id !== noteId);
    let nextForLater = forLater.filter((id) => id !== noteId);
    if (targetZone === "forNow") nextForNow = [...nextForNow, noteId];
    if (targetZone === "forLater") nextForLater = [...nextForLater, noteId];

    setRoomAnswer("message", { forNow: nextForNow, forLater: nextForLater });
    setTagsForRoom("msg", nextForNow.map((cid) => `msg:${cid}`));
  };

  // Tap-to-cycle: source → forNow → forLater → source
  const tap = (noteId) => {
    if (forNow.includes(noteId)) return move(noteId, "forLater");
    if (forLater.includes(noteId)) return move(noteId, "source");
    if (forNow.length < FOR_NOW_MAX) return move(noteId, "forNow");
    return move(noteId, "forLater");
  };

  // The interaction area is aligned to the whiteboard in the source image.
  // Whiteboard bounds in source image (percentages): x ~40-76%, y ~17-51%.
  // Coordinates below are in pre-camera-transform space so the overlay
  // moves and scales with the camera.
  return (
    <div
      data-testid="whiteboard-canvas"
      className="absolute"
      style={{
        left: "40%",
        top: "17%",
        width: "36%",
        height: "34%",
        // Subtle perspective skew to align with the whiteboard's slight tilt
        transform: "perspective(1400px) rotateY(-2deg) rotateX(0.5deg)",
        transformOrigin: "50% 50%",
      }}
    >
      <div
        className="w-full h-full flex flex-row gap-2 p-2"
        style={{
          background: "rgba(255,255,255,0.02)",
          borderRadius: "4px",
        }}
      >
        {/* LEFT HALF — Available notes grid */}
        <div className="w-1/2 h-full flex">
          <SourceGrid
            notes={source.map(noteById)}
            indexOf={indexOf}
            onDrop={move}
            onTap={tap}
            interactive={canProceed || forNow.length > 0 || answers.message?.introDone}
          />
        </div>

        {/* RIGHT HALF — two stacked coloured buckets */}
        <div className="w-1/2 h-full flex flex-col gap-2">
          <Bucket
            id="forNow"
            title="For Now"
            count={forNow.length}
            cap={FOR_NOW_MAX}
            notes={forNow.map(noteById)}
            indexOf={indexOf}
            onDrop={move}
            onTap={tap}
            interactive={canProceed || forNow.length > 0 || answers.message?.introDone}
            bgFrom="#7C8A4E"
            bgTo="#5C6A38"
            borderColor="rgba(255,255,255,0.35)"
          />
          <Bucket
            id="forLater"
            title="For Later"
            count={forLater.length}
            notes={forLater.map(noteById)}
            indexOf={indexOf}
            onDrop={move}
            onTap={tap}
            interactive={canProceed || forNow.length > 0 || answers.message?.introDone}
            bgFrom="#D4A85A"
            bgTo="#B08838"
            borderColor="rgba(255,255,255,0.35)"
          />
        </div>
      </div>
    </div>
  );
}

// -------- Renders OUTSIDE the camera — handles intro click-to-advance --------
function MessagePanel() {
  const { answers, setRoomAnswer, setCanProceed, setOverrideMessage } = useAudit();
  const forNow = answers.message?.forNow || [];
  const introStep = answers.message?.introStep ?? 0;
  const successStep = answers.message?.successStep ?? 0;
  const introDone = introStep >= NOTES_INTRO_STEPS.length;
  const done = forNow.length === FOR_NOW_MAX;
  const successDone = successStep >= NOTES_SUCCESS_STEPS.length;

  // Sync bubble + Continue gate with the current phase.
  useEffect(() => {
    // Phase 1 — intro is still playing.
    if (!introDone) {
      setOverrideMessage(NOTES_INTRO_STEPS[introStep]);
      setCanProceed(false);
      return;
    }
    // Phase 2 — intro finished, waiting for the user to place 5 notes.
    if (!done) {
      setOverrideMessage("");
      setCanProceed(false);
      return;
    }
    // Phase 3 — 5 notes placed, success lines play one at a time.
    if (!successDone) {
      setOverrideMessage(NOTES_SUCCESS_STEPS[successStep]);
      setCanProceed(false);
      return;
    }
    // Phase 4 — all success beats seen, keep the final line and unlock Continue.
    setOverrideMessage(NOTES_SUCCESS_STEPS[NOTES_SUCCESS_STEPS.length - 1]);
    setCanProceed(true);
  }, [introStep, introDone, done, successStep, successDone, setCanProceed, setOverrideMessage]);

  // If the user drops back below 5 after seeing the success beats, reset them.
  useEffect(() => {
    if (!done && successStep > 0) {
      setRoomAnswer("message", { successStep: 0 });
    }
  }, [done, successStep, setRoomAnswer]);

  const inCatcherPhase = !introDone || (done && !successDone);
  const advance = () => {
    if (!introDone) {
      setRoomAnswer("message", { introStep: introStep + 1 });
      return;
    }
    if (done && !successDone) {
      setRoomAnswer("message", { successStep: successStep + 1 });
    }
  };

  if (!inCatcherPhase) return null;

  const totalSteps = !introDone ? NOTES_INTRO_STEPS.length : NOTES_SUCCESS_STEPS.length;
  const currentStep = !introDone ? introStep + 1 : successStep + 1;

  return (
    <button
      type="button"
      onClick={advance}
      data-testid="intro-advance"
      aria-label="Continue"
      className="absolute inset-0 z-[25] cursor-pointer bg-transparent pointer-events-auto"
    >
      <span
        aria-hidden="true"
        className="absolute bottom-24 md:bottom-28 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.28em] text-paper/70 animate-pulse"
      >
        tap to continue &nbsp;·&nbsp; {currentStep} / {totalSteps}
      </span>
    </button>
  );
}


function OpeningPanel() {
  return (
    <RoomOverlay number={6} kicker="Opening" title="What is the first 30 seconds doing?" side="right">
      <div className="space-y-2.5">
        <DemoChoice roomId="opening" choiceId="A" label="Landing an idea worth listening to" tag="open:landing" />
        <DemoChoice roomId="opening" choiceId="B" label="Housekeeping and agenda"           tag="open:housekeeping" />
        <DemoChoice roomId="opening" choiceId="C" label="Apologising or warming up"         tag="open:apology" />
      </div>
    </RoomOverlay>
  );
}

function ReflectionPanel() {
  const { selfReflection, setSelfReflection } = useAudit();
  return (
    <RoomOverlay number={7} kicker="Reflection" title="What did you notice in yourself while watching?" side="right">
      <textarea
        value={selfReflection}
        onChange={(e) => setSelfReflection(e.target.value)}
        rows={5}
        placeholder="Write freely — one line or a paragraph. This shapes the final map."
        data-testid="reflection-input"
        className="w-full bg-paper border border-ink/15 px-4 py-3 font-body text-ink focus:outline-none focus:border-terracotta transition-colors resize-y"
      />
    </RoomOverlay>
  );
}

function ExtraContextPanel() {
  const { extraContext, setExtraContext } = useAudit();
  return (
    <RoomOverlay number={8} kicker="Extra Context" title="Which part of your communication would you most like to explore or improve next?" side="right">
      <textarea
        value={extraContext}
        onChange={(e) => setExtraContext(e.target.value)}
        rows={5}
        placeholder="Audience, stakes, time pressure, format, past feedback…"
        data-testid="extra-context-input"
        className="w-full bg-paper border border-ink/15 px-4 py-3 font-body text-ink focus:outline-none focus:border-terracotta transition-colors resize-y"
      />
    </RoomOverlay>
  );
}

function PerformanceMapPanel() {
  const { answers, tags, reflections, setPatternRead } = useAudit();

  // Deterministic read — computed once when the map opens, stored globally
  // for the later full personalised report.
  const read = useMemo(
    () => buildPatternRead({ answers, tags, reflections }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useEffect(() => { setPatternRead(read); }, [read, setPatternRead]);

  const Section = ({ kicker, finding, testId }) => (
    <div data-testid={testId} className="border-t border-ink/12 pt-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.26em] text-terracotta mb-1.5">
        {kicker}
      </p>
      <p className="font-serif text-lg md:text-xl leading-snug text-ink mb-1.5">
        {finding.title}
      </p>
      <p className="font-body text-sm leading-relaxed text-ink-soft">
        {finding.explanation}
      </p>
    </div>
  );

  return (
    <RoomOverlay number={8} kicker="Performance Map" title="What we noticed — a first read." side="left">
      <div className="space-y-4 max-h-[420px] overflow-y-auto pr-1">
        <Section kicker="Natural Strength"            finding={read.naturalStrength}       testId="pattern-strength" />
        <Section kicker="A Pattern to Explore"        finding={read.patternToWatch}        testId="pattern-watch" />
        <Section kicker="Your First Development Focus" finding={read.firstDevelopmentFocus} testId="pattern-focus" />
        <p className="font-body text-xs text-ink-soft/80 italic pt-1">
          This preview shows three highlights from your Audit. The full personalised
          read draws on everything you shared.
        </p>
      </div>
    </RoomOverlay>
  );
}

// ---------- Room configs ----------------------------------------------------
export const ROOM_CONFIGS = {
  message: {
    camera: "whiteboard",
    pose: "presentingAlt",
    message: NOTES_INTRO_STEPS[0],
    bubbleSide: "right",
    Panel: MessagePanel,
    BoardOverlay: MessageBoardOverlay,
  },
  structure: {
    camera: "whiteboard",
    pose: "presenting",
    message: "",
    bubbleSide: "right",
    Panel: null, // handled by dedicated RehearsalRoomStructure component
  },
  voice: {
    camera: "wide",
    pose: "speaking",
    message: "",
    bubbleSide: "right",
    Panel: null, // handled by dedicated RehearsalRoomVoice component
  },
  gesture: {
    camera: "wide",
    pose: "guidePoint",
    message: "",
    bubbleSide: "right",
    Panel: null, // handled by dedicated RehearsalRoomGesture component
  },
  space: {
    camera: "floor",
    pose: "presentingWide",
    message: "",
    bubbleSide: "right",
    Panel: null, // handled by dedicated RehearsalRoomSpace component
  },
  opening: {
    camera: "wide",
    pose: "presentingAlt",
    message: "",
    bubbleSide: "right",
    Panel: null, // handled by dedicated RehearsalRoomOpening component
  },
  reflection: {
    camera: "wide",
    pose: "thinking",
    message: "",
    bubbleSide: "right",
    Panel: null, // handled by dedicated RehearsalRoomReflection component
  },
  extraContext: {
    camera: "wide",
    pose: "listening",
    message: "Tell me the context — audience, stakes, format. Everything shapes the map.",
    bubbleSide: "right",
    Panel: ExtraContextPanel,
  },
  performanceMap: {
    camera: "desk",
    pose: "thinking",
    message: "Here's what we saw together. This is where your pattern begins to show up.",
    bubbleSide: "left",
    Panel: PerformanceMapPanel,
  },
};
